// ─────────────────────────────────────────────────────────────────────────────
// Payroll module — collection names, enums, seed settings, JSDoc typedefs.
//
// KEEP THIS FILE FREE OF FIREBASE IMPORTS. The pure pay engine
// (src/utils/payCalculator.js and friends) and the Excel acceptance harness both
// import it, and neither may drag in a Firestore SDK. This is the same rule
// src/constants/attendance.js states for the legacy engine — and it is why the
// dependency runs ONE WAY: src/constants/schema.js imports PAYROLL_COLLECTIONS
// from here and spreads it into COLLECTIONS. Never import schema.js from this
// file or from anything downstream of it in the engine; schema.js imports
// `serverTimestamp` from firebase/firestore.
//
// Every threshold, cap, multiplier and label below is a DEFAULT SEED, not a
// constant. Live values come from `paySettings` documents resolved per worker
// per date (see src/utils/paySettingsResolver.js). Nothing in the engine may
// read a number directly from this file at calculation time — it reads the
// resolved settings object. These defaults exist so a fresh tenant has a
// working ruleset and so tests have a fixture.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bumped whenever a change to the engine could alter a previously computed
 * figure. Stamped onto every DayResult/MonthSummary so a re-run that produces
 * different numbers is traceable to a code change rather than a settings change.
 */
export const PAY_ENGINE_VERSION = 1;

/**
 * The role allowed to edit pay rates, business rules and the holiday calendar.
 * Everything else in Attendance is gated by the `attendance` feature key; this
 * is the narrower gate for the decisions that set what a month is worth.
 *
 * Moved here from the retired src/constants/attendance.js, which went with the
 * legacy module. It must stay in step with isBossControl() in firestore.rules —
 * the client check is convenience, the rule is the boundary.
 */
export const ATTENDANCE_SUPER_ROLE = 'bosscontrol';

/** Firestore collection names owned by the payroll module. */
export const PAYROLL_COLLECTIONS = Object.freeze({
  PUNCH_EVENTS:    'punchEvents',
  DAY_WORKPLACES:  'dayWorkplaces',
  PAY_RATES:       'payRates',
  PAY_SETTINGS:    'paySettings',
  PAY_NAME_ALIASES:'payNameAliases',
  PUNCH_DEVICES:   'punchDevices',
});

// ─────────────────────────────────────────────────────────────────────────────
// Enums
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Punch event types. `void` is how a correction is made: append a `void` row
 * pointing at the bad event, then append the replacement. The log itself is
 * never edited or deleted — see the `punchEvents` block in firestore.rules.
 */
export const PUNCH_TYPE = Object.freeze({
  NORMAL_IN:   'normal_in',
  NORMAL_OUT:  'normal_out',
  OT_IN:       'ot_in',
  OT_OUT:      'ot_out',
  OUTSIDE_IN:  'outside_in',
  OUTSIDE_OUT: 'outside_out',
  DRIVER_IN:   'driver_in',
  DRIVER_OUT:  'driver_out',
  VOID:        'void',
});

/** Where a press physically happened. Drives the verification chain. */
export const LOCATION_CONTEXT = Object.freeze({
  FACTORY: 'factory',
  SITE:    'site',
  UNKNOWN: 'unknown',
});

/** What kind of device produced the press. */
export const DEVICE_KIND = Object.freeze({
  KIOSK:  'kiosk',
  MOBILE: 'mobile',
  IMPORT: 'import',
  MANUAL: 'manual',
});

/** Provenance of an event. `excel_import` rows are historical backfill. */
export const PUNCH_SOURCE = Object.freeze({
  KIOSK:        'kiosk',
  MOBILE:       'mobile',
  MANUAL:       'manual',
  EXCEL_IMPORT: 'excel_import',
});

/** What a worker-day fundamentally is, before any hours are computed. */
export const DAY_CATEGORY = Object.freeze({
  WORK:          'work',
  LEAVE:         'leave',
  NO_PUNCH_CARD: 'no_punch_card',
  ABSENT:        'absent',
});

/** Day classification. Public holiday beats Sunday. */
export const DAY_TYPE = Object.freeze({
  WEEKDAY:        'weekday',
  SATURDAY:       'saturday',
  SUNDAY:         'sunday',
  PUBLIC_HOLIDAY: 'publicHoliday',
});

/** Worker pay model. */
export const RATE_TYPE = Object.freeze({
  DAILY:   'daily',
  MONTHLY: 'monthly',
});

/**
 * Pay-settings scope kinds, ORDERED least- to most-specific. The array index is
 * the `specificity` value stored on each settings doc, and resolution deep-merges
 * ascending by it — so a worker-scoped override wins over a company-scoped one.
 *
 * NOTE: specificity beats recency. A worker-scoped doc effective 2024 outranks a
 * company-scoped doc effective 2026. That is deliberate (an individual
 * arrangement should not be silently undone by a later blanket change) and it is
 * counter-intuitive enough to have its own test.
 */
export const PAY_SCOPE_ORDER = Object.freeze(['all', 'role', 'company', 'rateType', 'worker']);

export const PAY_SCOPE = Object.freeze({
  ALL:       'all',
  ROLE:      'role',
  COMPANY:   'company',
  RATE_TYPE: 'rateType',
  WORKER:    'worker',
});

/**
 * The pay buckets, in the order the Excel `Individual` sheet's summary block
 * lists them. A MonthSummary's `buckets` is a straight sum of DayResult rows into
 * these keys, so the two can be reconciled line for line.
 */
export const PAY_BUCKET = Object.freeze({
  NORMAL_PAY:         'normalPay',
  DEDUCTIBLE_HOUR:    'deductibleHour',
  REGULAR_OT_PAY:     'regularOtPay',
  DRIVER_OT:          'driverOt',
  SUNDAY_PAY:         'sundayPay',
  PUBLIC_HOLIDAY_PAY: 'publicHolidayPay',
  LEAVE_PAY:          'leavePay',
  FOOD_EXPENSE:       'foodExpense',
});

/**
 * How the acceptance harness classifies a disagreement between the calculator
 * and the workbook. Only CALCULATOR_BUG counts against the pass threshold.
 *
 * The 2026 workbook contains ZERO formulas — every hours column is a static
 * value written by the VBA macro — so a mismatch cannot be classified by
 * comparing a cell against its own formula. Classification is therefore by
 * signature clustering: a mismatch pattern recurring across many rows is a bug
 * in our engine; an isolated one whose input signature matches elsewhere is a
 * hand-edit in the source.
 */
export const MISMATCH_CATEGORY = Object.freeze({
  CALCULATOR_BUG:    'calculator_bug',
  DATA_ANOMALY:      'data_anomaly',
  SOURCE_DATA_GAP:   'source_data_gap',
  KNOWN_QUIRK:       'known_quirk',
  ROUNDING_BOUNDARY: 'rounding_boundary',
});

// ─────────────────────────────────────────────────────────────────────────────
// Workbook vocabulary → app vocabulary
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The workbook writes company names in full; COMPANIES in schema.js uses short
 * forms for two of the three. Unknown values are passed through with a warning
 * by the importer rather than dropped or coerced — a new company appearing in
 * the source must surface, not vanish.
 */
export const COMPANY_ALIASES = Object.freeze({
  'stema furniture':   'Stema Furniture',
  'l&n furniture':     'L&N',
  'l&n':               'L&N',
  's&j manufacturing': 'S&J',
  's&j':               'S&J',
  'stemcrafter':       'Stemcrafter',
});

/**
 * Leave labels as they appear in the month sheets' "Work Place" column, mapped
 * to (leaveType, halfDay). The workbook encodes half-days as distinct labels;
 * the app models them as a flag on a single type, matching LEAVE_TYPE in
 * schema.js and the `halfDay` field on `leaveRecords`.
 *
 * Keys are normalised (lowercased, whitespace-collapsed) by normalizeLeaveLabel.
 * Paid hours are NOT here — they live in pay settings, because they are a
 * business rule a tenant can change, not a property of the label.
 */
export const LEAVE_LABEL_MAP = Object.freeze({
  'mc':                     { leaveType: 'sick',            halfDay: false },
  'halfday mc':             { leaveType: 'sick',            halfDay: true  },
  'annual leave':           { leaveType: 'annual',          halfDay: false },
  'halfday annual leave':   { leaveType: 'annual',          halfDay: true  },
  'urgent leave':           { leaveType: 'urgent',          halfDay: false },
  'halfday urgent leave':   { leaveType: 'urgent',          halfDay: true  },
  'no pay leave':           { leaveType: 'no_pay',          halfDay: false },
  'halfday no pay leave':   { leaveType: 'no_pay',          halfDay: true  },
  'hospitalization':        { leaveType: 'hospitalization', halfDay: false },
  'home leave':             { leaveType: 'home',            halfDay: false },
});

/** Prefix in the Work Place column that marks a non-working day. */
export const LEAVE_PREFIX = '(leave)';
export const NO_PUNCH_CARD_PREFIX = '(no punch card)';

/**
 * Normalise a Work Place cell for lookup: lowercase, collapse whitespace, strip
 * the "(Leave) - " / "(No Punch Card) - " prefix. Returns null for an ordinary
 * workplace. Pure — no dependency on the importer.
 *
 * @param {*} raw
 * @returns {{ category: string, label: string }|null}
 */
export function parseWorkplaceCategory(raw) {
  const s = String(raw ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!s) return null;
  if (s.startsWith(LEAVE_PREFIX)) {
    return { category: DAY_CATEGORY.LEAVE, label: s.slice(LEAVE_PREFIX.length).replace(/^\s*-\s*/, '').trim() };
  }
  if (s.startsWith(NO_PUNCH_CARD_PREFIX)) {
    return { category: DAY_CATEGORY.NO_PUNCH_CARD, label: s.slice(NO_PUNCH_CARD_PREFIX.length).replace(/^\s*-\s*/, '').trim() };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default pay settings — the Stema seed
//
// Every value below was verified against the 2026 workbook (8,620 day-rows, 9
// populated months) rather than read off its User Guide sheet, which is stale in
// two places (it states a 4-hour Sunday/PH cap and describes the OT grace from
// the opposite direction). Where the two disagree, the data won.
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_PAY_SETTINGS = Object.freeze({
  clockIn: Object.freeze({
    // Anything earlier than this is credited from here — a worker who arrives at
    // 07:02 is paid from 08:00, never before.
    earliestCreditTime: '08:00',
    // Minute-of-hour at/after which the cutoff rounds UP to the next hour.
    // The workbook's own User Guide says ":15 rounds down"; the single observed
    // :15 sample rounds UP. The boundary is only pinned between :11 (down) and
    // :15 (up), so this ships as a tunable rather than a constant. Exposure is
    // small — 519 of 8,620 rows clock in at/after 08:00, and 388 of those are
    // exactly on the hour. The harness's `rounding_boundary` category exists to
    // size it against real data.
    roundUpAtMinute: 16,
    // Late arrivals round up properly: 09:47 -> 10:00, 16:44 -> 17:00. They are
    // NOT floored back to 08:00. (The legacy engine gets this wrong.)
    floorToEarliest: true,
  }),

  normalDay: Object.freeze({
    dayEndCap: '17:00',
    // Deducted from the span when the shift runs past the window's end.
    lunch: Object.freeze({ start: '12:00', end: '13:00' }),
    graceMinutes: 50,
    granularity: 'floor_hour',
    maxHours: 8,
    // Verified against the data: 18/18 short daily-rated days and 19/19 short
    // monthly-rated days carry a deduction. The legacy engine restricts this to
    // monthly-rated, which is wrong.
    deductibleAppliesTo: Object.freeze(['daily', 'monthly']),
    countsAsWorkingDay: true,
  }),

  weekdayOt: Object.freeze({
    // Counted flexibly from the OT-in press, not from a fixed 17:00.
    anchor: 'ot_punch',
    // ...but never before the OT window opens. A press at 16:40 still counts
    // from 17:00, so leaving early and pressing OT cannot manufacture hours.
    earliestStart: '17:00',
    // Leftover >= 50 min rounds up to the next whole hour. Verified at scale:
    // 337 round-ups vs 1 at +50m; 232 vs 0 at +55m; and never at <= 48m.
    graceMinutes: 50,
    granularity: 'floor_hour_grace',
    maxHours: 4,
    multiplier: 1.5,
    displayName: 'Weekday OT',
    bucket: PAY_BUCKET.REGULAR_OT_PAY,
  }),

  driverOt: Object.freeze({
    // Anchored on a FIXED clock time, not a press — the workbook always records
    // Driver Time In as 17:00 regardless of when the trip actually started.
    anchor: 'fixed_time',
    anchorTime: '17:00',
    // The same 50-minute grace as every other OT rule, but with a half-hour
    // floor: any trip at all earns 0.5 h, and the next whole hour is reached at
    // 50 minutes. Fitted against all 502 driver rows in the workbook (95.4%);
    // the 0.5-to-1 boundary sits between 48 and 51 minutes, and every span from
    // 20 to 41 minutes earns exactly 0.5.
    //
    // It is emphatically NOT floor-to-30-minute-blocks, which was the obvious
    // reading of a handful of rows and scores only 53%.
    granularity: 'floor_hour_grace',
    graceMinutes: 50,
    minimumHours: 0.5,
    // A trip shorter than this earns nothing at all. The shortest trip the
    // workbook ever records is 20 minutes; below that its Driver OT cell is
    // left empty. Made explicit here rather than left implied, so a very short
    // hop does not quietly earn half an hour.
    minimumMinutes: 20,
    blockMinutes: 30,
    maxHours: null,
    multiplier: 1.5,
    // Driver OT and weekday OT COEXIST on the same day: the driver trip is the
    // site->factory ride that happens BEFORE OT starts, so the two are
    // sequential, not overlapping. Verified on multiple worker-days.
    coexistsWith: Object.freeze(['weekdayOt']),
    displayName: 'Driver OT',
    bucket: PAY_BUCKET.DRIVER_OT,
  }),

  outsideOt: Object.freeze({
    // Hand-typed in Excel with no macro behind it, and inconsistently: 58 min
    // was recorded as 0.5 h, 1 h 32 m as 1 h. There is no formula to reproduce,
    // so historical values are IMPORTED VERBATIM. `granularity` below only takes
    // effect once outside OT is captured by button press rather than typed.
    source: 'imported_value',
    granularity: 'half_hour',
    blockMinutes: 30,
    maxHours: 4,
    multiplier: 1.5,
    displayName: 'Outside OT',
    bucket: PAY_BUCKET.REGULAR_OT_PAY,
  }),

  sundayPh: Object.freeze({
    // The whole day is OT — there is no normal-day component. Counted from the
    // CUTOFF, not the raw clock-in (the User Guide is explicit, and the data
    // agrees).
    countFrom: 'cutoff',
    graceMinutes: 50,
    granularity: 'floor_hour_grace',
    // Cap 8, NOT the User Guide's stale 4. Confirmed by PH rows on 1 Jan and
    // 1 Jun showing 8 OT hours against 0 normal hours.
    maxHours: 8,
    // A Sunday is 2x for EVERYONE, regardless of rate type.
    multiplier: 2,
    countsAsWorkingDay: false,

    // A public holiday is where the rate types diverge, and only there. For
    // monthly-rated workers PH hours land in the NORMAL bucket at 1x — already
    // inside their salary — and count as a working day so the month is not
    // deducted; for daily-rated they land in the Sunday/PH bucket at 2x.
    // Confirmed structurally on 17-18 Feb, where monthly workers' PH hours
    // appear in the sheet's Normal column and daily workers' in the OT column.
    //
    // This quirk does NOT extend to Sundays. Applying it there would silently
    // halve every monthly-rated worker's Sunday pay.
    publicHoliday: Object.freeze({
      multiplierByRateType:         Object.freeze({ daily: 2,          monthly: 1 }),
      bucketByRateType:             Object.freeze({ daily: 'sundayPh', monthly: 'normal' }),
      countsAsWorkingDayByRateType: Object.freeze({ daily: false,      monthly: true }),
    }),
    displayName: 'Sunday / Public Holiday',
  }),

  leave: Object.freeze({
    // Paid hours per leave type, full day and half day. Half-day No Pay pays 4 h
    // while full-day No Pay pays 0 — not a typo: a half-day no-pay worker worked
    // the other half.
    // `countsAsWorkingDay` decides whether the day counts toward the month's
    // required working days for a monthly-rated worker. Paid leave does, so an
    // approved absence is not deducted on top of being paid; unpaid leave does
    // not. This is the spec's per-leave-type counts_as_working_day flag.
    paidHoursByType: Object.freeze({
      sick:            Object.freeze({ full: 8, half: 4, countsAsWorkingDay: true }),
      annual:          Object.freeze({ full: 8, half: 4, countsAsWorkingDay: true }),
      urgent:          Object.freeze({ full: 8, half: 4, countsAsWorkingDay: true }),
      hospitalization: Object.freeze({ full: 8, half: 4, countsAsWorkingDay: true }),
      no_pay:          Object.freeze({ full: 0, half: 4, countsAsWorkingDay: false }),
      home:            Object.freeze({ full: 0, half: 0, countsAsWorkingDay: false }),
    }),
    // A "(No Punch Card)" day is not leave — it is a missing record. Zero paid.
    noPunchCardHours: 0,
    fullDayWorkingDays: 1,
    halfDayWorkingDays: 0.5,
    // Unknown labels pay nothing and raise a parse warning rather than throwing.
    unknownLabelHours: 0,
    bucket: PAY_BUCKET.LEAVE_PAY,
  }),

  rateDerivation: Object.freeze({
    // Both are settings, never constants: monthly -> daily uses daysPerMonth,
    // daily -> hourly uses hoursPerDay. Verified: a $3,300 monthly base yields
    // $126.9231/day (3300/26) and $15.8654/hr (126.9231/8).
    hoursPerDay: 8,
    daysPerMonth: 26,
  }),

  foodExpense: Object.freeze({
    displayName: 'Meal allowance',
    amount: 5,
    weekdayMinOtHours: 3,
    sundayPhMinHours: 8,
  }),

  // The workbook's own month -> working-days table (Names&Workplace cols U/V).
  // Read from the sheet at import time and seeded here so the two never drift.
  workingDaysByMonth: Object.freeze({
    '01': 27, '02': 24, '03': 26, '04': 26, '05': 27, '06': 25,
    '07': 27, '08': 26, '09': 26, '10': 27, '11': 25, '12': 27,
  }),

  // Nightly auto-close for an ot_start with no matching ot_end. Zero OT, flagged
  // unresolved, surfaced in the next morning's admin queue. A published rule,
  // never presented as an estimate of when the worker left.
  unresolvedSession: Object.freeze({
    cutoffTime: '22:00',
    creditHours: 0,
  }),

  verification: Object.freeze({
    // 'off' | 'soft' | 'hard'. Soft for rollout: evidence recorded and mismatches
    // flagged, nothing gated. A press is never discarded in any mode.
    mode: 'soft',
  }),
});

// ─────────────────────────────────────────────────────────────────────────────
// JSDoc typedefs (documentation only — no runtime effect)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {'weekday'|'saturday'|'sunday'|'publicHoliday'} DayType
 * @typedef {'daily'|'monthly'} RateType
 * @typedef {'work'|'leave'|'no_punch_card'|'absent'} DayCategory
 */

/**
 * One worker-day, projected from the append-only punch log plus its
 * `dayWorkplaces` doc. Times are INTEGER MINUTES FROM LOCAL MIDNIGHT — never
 * Date objects. The service layer does the one Timestamp <-> minutes conversion
 * at the Firestore boundary, and the importer reads Excel serial fractions
 * directly, so no timezone arithmetic ever reaches the engine.
 *
 * @typedef {Object} DayInput
 * @property {string} workerId
 * @property {string} workDate              'YYYY-MM-DD' — the PAYROLL day
 * @property {DayCategory} dayCategory
 * @property {string|null} leaveType
 * @property {boolean} halfDay
 * @property {number|null} clockInMin        raw press, unrounded
 * @property {number|null} clockOutMin
 * @property {number|null} otInMin
 * @property {number|null} otOutMin
 * @property {number|null} driverOutMin
 * @property {number|null} outsideOtHours    IMPORTED value, never computed
 * @property {string} workplace
 * @property {string} workplace2
 * @property {Object} manualOverrides        e.g. { normalHours: 7 }
 */

/**
 * @typedef {Object} DerivedRates
 * @property {number} dailyRate
 * @property {number} hourlyRate
 * @property {number} otHourlyRate
 * @property {number} sundayHourlyRate
 * @property {string} payRatesDocId
 * @property {string} effectiveFrom
 */
